import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Screen } from "../components/Screen";
import { CreditProgramCard } from "../components/CreditProgramCard";
import { api } from "../api/client";
import { useAppStore } from "../state/store";
import { track } from "../analytics/track";
import { formatUAH } from "../utils/format";
import type { CreditCalculationResult, CreditProgram, PurchaseScenario } from "@karat/shared";

/**
 * п.8-12 ТЗ: сценарій покупки -> для "кредит" підвантажуємо ВСІ реально доступні програми
 * (зараз — тільки "Toyota Легко", див. seedCreditPrograms.ts) і дозволяємо порівняти й
 * налаштувати аванс/строк. Кредит завжди рахується через /api/credit/calculate — той самий
 * shared creditEngine, що й у майбутньому Web App (єдине джерело правди).
 */
export function Credit() {
  const navigate = useNavigate();
  const selectedCar = useAppStore((s) => s.selectedCar);
  const creditSelection = useAppStore((s) => s.creditSelection);
  const setCreditSelection = useAppStore((s) => s.setCreditSelection);

  const [scenario, setScenario] = useState<PurchaseScenario>("credit");
  const [programs, setPrograms] = useState<CreditProgram[] | null>(null);
  const [results, setResults] = useState<Record<string, CreditCalculationResult>>({});
  const [activeProgramId, setActiveProgramId] = useState<string | null>(null);
  const [advancePercent, setAdvancePercent] = useState<number | null>(null);
  const [termMonths, setTermMonths] = useState<number | null>(null);
  const [activeResult, setActiveResult] = useState<CreditCalculationResult | null>(null);

  useEffect(() => {
    if (!selectedCar) return;
    api
      .get<CreditProgram[]>(`/api/credit/programs?carPriceUAH=${selectedCar.priceUAH}`)
      .then(async (progs) => {
        setPrograms(progs);
        const entries = await Promise.all(
          progs.map(async (p) => {
            const term = p.availableTermsMonths[p.availableTermsMonths.length - 1];
            const result = await api.post<CreditCalculationResult>("/api/credit/calculate", {
              carPriceUAH: selectedCar.priceUAH,
              programId: p.id,
              termMonths: term,
              advancePercent: p.defaultAdvancePercent,
            });
            return [p.id, result] as const;
          })
        );
        setResults(Object.fromEntries(entries));
      })
      .catch(() => setPrograms([]));
  }, [selectedCar]);

  // Відновлюємо попередній вибір програми/параметрів (п.27 ТЗ), якщо він стосується цього ж авто
  useEffect(() => {
    if (creditSelection && programs?.some((p) => p.id === creditSelection.programId)) {
      selectProgram(creditSelection.programId, creditSelection.advancePercent, creditSelection.termMonths);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programs]);

  function selectProgram(programId: string, advance?: number, term?: number) {
    const program = programs?.find((p) => p.id === programId);
    if (!program || !selectedCar) return;
    const t = term ?? program.availableTermsMonths[program.availableTermsMonths.length - 1];
    const a = advance ?? program.defaultAdvancePercent;
    setActiveProgramId(programId);
    setAdvancePercent(a);
    setTermMonths(t);
    track(program.code === "toyota_legko" ? "select_toyota_legko" : "select_toyota_grace", { programId });
    recalculate(programId, a, t);
  }

  async function recalculate(programId: string, advance: number, term: number) {
    if (!selectedCar) return;
    const result = await api.post<CreditCalculationResult>("/api/credit/calculate", {
      carPriceUAH: selectedCar.priceUAH,
      programId,
      termMonths: term,
      advancePercent: advance,
    });
    setActiveResult(result);
    setCreditSelection({ programId, termMonths: term, advancePercent: advance });
  }

  if (!selectedCar) {
    return (
      <Screen title="Розрахувати кредит" showBack>
        <div className="empty-state">
          <div className="empty-state-icon">🚗</div>
          Спочатку оберіть автомобіль — тоді розрахунок буде точним.
          <div style={{ marginTop: 16 }}>
            <button className="btn btn-primary" onClick={() => navigate("/cars/new")}>
              Обрати автомобіль
            </button>
          </div>
        </div>
      </Screen>
    );
  }

  const activeProgram = programs?.find((p) => p.id === activeProgramId);

  return (
    <Screen title="Розрахувати кредит" showBack>
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 700 }}>
          {selectedCar.modelName} {selectedCar.trimName}
        </div>
        <div className="price" style={{ marginTop: 4 }}>
          {formatUAH(selectedCar.priceUAH)}
        </div>
      </div>

      <div className="section-title">Як Ви плануєте придбати автомобіль?</div>
      <div className="chip-row" style={{ marginBottom: 16 }}>
        <button className={`chip ${scenario === "cash" ? "active" : ""}`} onClick={() => setScenario("cash")}>
          За власні кошти
        </button>
        <button className={`chip ${scenario === "credit" ? "active" : ""}`} onClick={() => setScenario("credit")}>
          У кредит
        </button>
        <button
          className={`chip ${scenario === "trade_in_advance" ? "active" : ""}`}
          onClick={() => {
            setScenario("trade_in_advance");
            track("start_trade_in", { fromCredit: true });
          }}
        >
          Trade-in як аванс
        </button>
      </div>

      {scenario === "cash" && (
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 14, color: "var(--color-text-secondary)" }}>До сплати одразу</div>
          <div className="price" style={{ fontSize: 24, margin: "6px 0 16px" }}>
            {formatUAH(selectedCar.priceUAH)}
          </div>
          <button className="btn btn-primary" onClick={() => navigate("/consultants?intent=proposal")}>
            Отримати пропозицію
          </button>
        </div>
      )}

      {scenario === "trade_in_advance" && (
        <div className="empty-state">
          Спершу подайте заявку на оцінку вашого авто — консультант визначить суму, яку можна
          використати як аванс.
          <div style={{ marginTop: 16 }}>
            <button className="btn btn-primary" onClick={() => navigate("/trade-in")}>
              Оцінити моє авто
            </button>
          </div>
        </div>
      )}

      {scenario === "credit" && (
        <>
          {programs === null && <div className="loading-row">Підбираємо доступні програми…</div>}
          {programs && programs.length === 0 && (
            <div className="empty-state">Для цієї ціни зараз немає доступних кредитних програм.</div>
          )}
          {programs && programs.length > 0 && (
            <>
              <div className="section-title">Доступні програми</div>
              <div className="card-list">
                {programs.map((p) => (
                  <CreditProgramCard
                    key={p.id}
                    // Для обраної програми показуємо ЖИВИЙ результат (activeResult), що враховує
                    // поточні аванс/строк з панелі налаштувань нижче — інакше картка порівняння
                    // застигала б на дефолтних умовах і розходилась із щойно зміненим слайдером
                    // (п.12 ТЗ: "після зміни параметра розрахунок повинен оновлюватися одразу").
                    result={(p.id === activeProgramId && activeResult) || results[p.id] || ({} as CreditCalculationResult)}
                    selected={p.id === activeProgramId}
                    onSelect={() => selectProgram(p.id)}
                  />
                ))}
              </div>

              {activeProgram && activeResult && advancePercent !== null && termMonths !== null && (
                <div className="card" style={{ padding: 16, marginTop: 16 }}>
                  <div className="section-title" style={{ margin: "0 0 8px" }}>
                    Налаштування — {activeProgram.displayName}
                  </div>

                  <div className="field">
                    <label>
                      Аванс: {formatUAH(activeResult.advanceUAH)} ({Math.round(advancePercent * 100)}%)
                    </label>
                    <input
                      type="range"
                      min={activeProgram.minAdvancePercent * 100}
                      max={activeProgram.maxAdvancePercent * 100}
                      value={advancePercent * 100}
                      onChange={(e) => {
                        const a = Number(e.target.value) / 100;
                        setAdvancePercent(a);
                        track("change_advance", { advancePercent: a });
                        recalculate(activeProgram.id, a, termMonths);
                      }}
                    />
                  </div>

                  <div className="field">
                    <label>Строк</label>
                    <div className="chip-row">
                      {activeProgram.availableTermsMonths.map((t) => (
                        <button
                          key={t}
                          className={`chip ${t === termMonths ? "active" : ""}`}
                          onClick={() => {
                            setTermMonths(t);
                            recalculate(activeProgram.id, advancePercent, t);
                          }}
                        >
                          {t} міс.
                        </button>
                      ))}
                    </div>
                  </div>

                  <button className="btn btn-primary" onClick={() => navigate("/consultants?intent=proposal")}>
                    Отримати пропозицію
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </Screen>
  );
}
